import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import {
  Plus,
  Search,
  MoreVertical,
  Users,
  Shield,
  Mail,
  Calendar,
  Loader2,
  Pencil,
  Trash2,
  UserPlus,
  ShieldAlert
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700', description: 'Full system access' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-700', description: 'Create & manage projects' },
  contributor: { label: 'Contributor', color: 'bg-emerald-100 text-emerald-700', description: 'Tasks & content' },
  viewer: { label: 'Viewer', color: 'bg-slate-100 text-slate-700', description: 'Read-only access' }
};

const UserForm = ({ user, onSubmit, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: user?.name || '',
    role: user?.role || 'contributor',
    password: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData };
      // Only include password if it's set (for create or password change)
      if (!data.password) {
        delete data.password;
      }
      await onSubmit(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Full Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Doe"
          required
          data-testid="user-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Email *</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@company.com"
          required
          data-testid="user-email-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Role *</Label>
        <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
          <SelectTrigger data-testid="user-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ROLE_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-2">
                  <span>{config.label}</span>
                  <span className="text-xs text-slate-400">- {config.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{isEditing ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
        <Input
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder={isEditing ? '••••••••' : 'Enter password'}
          required={!isEditing}
          minLength={6}
          data-testid="user-password-input"
        />
        <p className="text-xs text-slate-500">Minimum 6 characters</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} data-testid="save-user-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isEditing ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  );
};

export const UserManagementPage = () => {
  const { api, user: currentUser } = useAuth();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Redirect non-admins
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      toast.error('Access denied. Admin privileges required.');
    }
  }, [isAdmin, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (data) => {
    try {
      await api.post('/users/admin', data);
      toast.success('User created successfully');
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create user';
      toast.error(message);
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await api.put(`/users/${editingUser.id}`, data);
      toast.success('User updated successfully');
      setDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update user';
      toast.error(message);
      throw error;
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Shield className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
            User Management
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">{users.length} total users</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingUser(null); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <UserForm
              user={editingUser}
              onSubmit={editingUser ? handleUpdate : handleCreate}
              onCancel={() => { setDialogOpen(false); setEditingUser(null); }}
              isEditing={!!editingUser}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
        {Object.entries(ROLE_CONFIG).map(([role, config]) => (
          <Card key={role} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setRoleFilter(roleFilter === role ? 'all' : role)}>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-slate-500 truncate">{config.label}s</p>
                  <p className="text-xl md:text-2xl font-bold text-slate-900">{roleCounts[role] || 0}</p>
                </div>
                <Badge className={`${config.color} text-xs hidden sm:inline-flex`}>{config.label}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 md:gap-4 items-stretch sm:items-center">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 w-full"
            data-testid="search-users"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-role">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {Object.entries(ROLE_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users List - Mobile Card View / Desktop Table */}
      <Card className="mcc-card">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left p-4 font-medium text-slate-500 text-sm">User</th>
                <th className="text-left p-4 font-medium text-slate-500 text-sm">Role</th>
                <th className="text-left p-4 font-medium text-slate-500 text-sm">Created</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="font-medium">No users found</p>
                    <p className="text-sm">Try adjusting your search or filter</p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr 
                    key={user.id} 
                    className="border-b border-slate-50 hover:bg-slate-50"
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                          {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.name}</p>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Mail className="w-3.5 h-3.5" />
                            {user.email}
                          </div>
                        </div>
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={ROLE_CONFIG[user.role]?.color || 'bg-slate-100'}>
                        {ROLE_CONFIG[user.role]?.label || user.role}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {user.created_at ? format(parseISO(user.created_at), 'MMM d, yyyy') : '-'}
                      </div>
                    </td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${user.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(user)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          {user.id !== currentUser?.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onSelect={(e) => e.preventDefault()}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete User</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete <strong>{user.name}</strong>? This action cannot be undone.
                                    All their data will remain but they will no longer be able to log in.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => handleDelete(user.id)}
                                  >
                                    Delete User
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y">
          {filteredUsers.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium text-sm">No users found</p>
              <p className="text-xs">Try adjusting your search or filter</p>
            </div>
          ) : (
            filteredUsers.map(user => (
              <div 
                key={user.id} 
                className="p-3 hover:bg-slate-50"
                data-testid={`user-card-${user.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium shrink-0">
                      {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-medium text-slate-900 text-sm truncate">{user.name}</p>
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">You</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" data-testid={`user-actions-mobile-${user.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(user)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit User
                      </DropdownMenuItem>
                      {user.id !== currentUser?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onSelect={(e) => e.preventDefault()}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="mx-4">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete <strong>{user.name}</strong>? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                              <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                onClick={() => handleDelete(user.id)}
                              >
                                Delete User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2 mt-2 ml-11">
                  <Badge className={`${ROLE_CONFIG[user.role]?.color || 'bg-slate-100'} text-xs`}>
                    {ROLE_CONFIG[user.role]?.label || user.role}
                  </Badge>
                  <span className="text-xs text-slate-400">
                    {user.created_at ? format(parseISO(user.created_at), 'MMM d, yyyy') : '-'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Role Permissions Legend */}
      <Card className="mcc-card">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-sm md:text-base">
            <ShieldAlert className="w-4 h-4" />
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div className="p-3 md:p-4 bg-red-50 rounded-lg">
              <Badge className="bg-red-100 text-red-700 mb-2 text-xs">Admin</Badge>
              <ul className="text-xs md:text-sm text-slate-600 space-y-0.5 md:space-y-1">
                <li>• Full system access</li>
                <li>• Manage all users</li>
                <li>• Delete projects/tasks</li>
                <li className="hidden sm:list-item">• View all reports</li>
              </ul>
            </div>
            <div className="p-3 md:p-4 bg-blue-50 rounded-lg">
              <Badge className="bg-blue-100 text-blue-700 mb-2 text-xs">Manager</Badge>
              <ul className="text-xs md:text-sm text-slate-600 space-y-0.5 md:space-y-1">
                <li>• Create/edit projects</li>
                <li>• Create/edit events</li>
                <li>• Approve content</li>
                <li className="hidden sm:list-item">• View all reports</li>
              </ul>
            </div>
            <div className="p-3 md:p-4 bg-emerald-50 rounded-lg">
              <Badge className="bg-emerald-100 text-emerald-700 mb-2 text-xs">Contributor</Badge>
              <ul className="text-xs md:text-sm text-slate-600 space-y-0.5 md:space-y-1">
                <li>• Create/edit tasks</li>
                <li>• Create/edit content</li>
                <li>• Upload assets</li>
                <li className="hidden sm:list-item">• View reports</li>
              </ul>
            </div>
            <div className="p-3 md:p-4 bg-slate-50 rounded-lg">
              <Badge className="bg-slate-100 text-slate-700 mb-2 text-xs">Viewer</Badge>
              <ul className="text-xs md:text-sm text-slate-600 space-y-0.5 md:space-y-1">
                <li>• View dashboard</li>
                <li>• View projects</li>
                <li>• View reports</li>
                <li className="hidden sm:list-item">• Read-only access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
